<template lang="pug">
.group-select
  el-popover(
    v-model="popoverVisible"
    placement="bottom-start"
    trigger="click"
    popper-class="group-select-popover"
    :disabled="disabled"
  )
    .group-select-dropdown
      .group-select-header
        el-checkbox(
          v-model="showHidden"
          @change="handleShowHiddenChange"
        ) {{ showHidden ? 'Скрыть неактивные' : 'Показать скрытые' }}

      .group-select-list
        .group-select-item(
          v-for="group in filteredGroups"
          :key="group.groupId"
          :class="{ 'is-active': currentValue === group.groupId }"
          @click="handleSelectGroup(group.groupId)"
        )
          span.group-name {{ group.groupName }}

          .group-actions(@click.stop)
            v-icon(
              :name="isGroupHidden(group.groupId) ? 'eyeSlash' : 'eye'"
              :size="16"
              :color="isGroupHidden(group.groupId) ? '#909399' : '#409EFF'"
              @click.native="toggleGroupVisibility(group.groupId)"
            )

    el-input(
      slot="reference"
      :value="selectedGroupName"
      placeholder="Выберите группу"
      readonly
      :disabled="disabled"
      suffix-icon="el-icon-arrow-down"
    )
</template>

<script>
import snakeizeKeys from '@/utils/snakeizeKeys';
import camelizeKeys from '@/utils/camelizeKeys';

export default {
  name: 'GroupSelect',

  props: {
    value: {
      type: [String, null],
      default: null
    },
    groups: {
      type: Array,
      default: () => []
    },
    hiddenGroups: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      popoverVisible: false,
      showHidden: false
    }
  },

  computed: {
    currentValue() {
      return this.value;
    },

    selectedGroupName() {
      const group = this.groups.find(g => g.groupId === this.currentValue);
      return group ? group.groupName : '';
    },

    filteredGroups() {
      // Если включен режим показа скрытых - показываем все
      if (this.showHidden) {
        return this.groups || [];
      }

      // Иначе фильтруем скрытые группы
      if (!Array.isArray(this.groups)) {
        return [];
      }

      return this.groups.filter(g => !this.isGroupHidden(g.groupId));
    }
  },

  methods: {
    isGroupHidden(groupId) {
      return this.hiddenGroups.includes(groupId);
    },

    handleSelectGroup(groupId) {
      this.$emit('input', groupId);
      this.popoverVisible = false;
    },

    handleShowHiddenChange(value) {
      // Если отключаем "показать скрытые" и текущая группа скрыта
      if (!value && this.currentValue && this.isGroupHidden(this.currentValue)) {
        this.$emit('input', null);
        this.$notify.warning({
          message: 'Выбранная группа была скрыта. Выберите другую группу.',
          duration: 3000
        });
      }
    },

    async toggleGroupVisibility(groupId) {
      const isCurrentlyHidden = this.isGroupHidden(groupId);

      try {
        const { data } = await this.axios.post('/api/ejournal/hidden-groups',
          snakeizeKeys({
            groupId: groupId,
            isHidden: !isCurrentlyHidden
          })
        );

        if (data.status) {
          const newHiddenGroups = camelizeKeys(data.data).hiddenGroups || [];
          const validHiddenGroups = Array.isArray(newHiddenGroups) ? newHiddenGroups : [];
          this.$emit('update:hidden-groups', validHiddenGroups);

          this.$notify.success({
            message: isCurrentlyHidden ? 'Группа показана' : 'Группа скрыта',
            duration: 2000
          });

          // Если скрываем текущую выбранную группу и не показываем скрытые
          if (!isCurrentlyHidden && this.currentValue === groupId && !this.showHidden) {
            this.$emit('input', null);
          }
        } else {
          this.$notify.error({
            message: data.messages || 'Ошибка обновления настроек',
            duration: 5000
          });
        }
      } catch (e) {
        let errorMessage = 'Ошибка при сохранении настроек';

        if (e.response && e.response.data && e.response.data.messages) {
          errorMessage = e.response.data.messages;
        }

        this.$notify.error({
          message: errorMessage,
          duration: 5000
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.group-select {
  ::v-deep .el-input__inner {
    cursor: pointer;
  }
}

.group-select-dropdown {
  min-width: 280px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.group-select-header {
  padding: 12px 16px;
  border-bottom: 1px solid #EBEEF5;
  background-color: #F5F7FA;
}

.group-select-list {
  max-height: 320px;
  overflow-y: auto;
}

.group-select-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F5F7FA;
  }

  &.is-active {
    background-color: #ECF5FF;
    color: #409EFF;
    font-weight: 500;
  }
}

.group-name {
  flex: 1;
  margin-right: 12px;
}

.group-actions {
  display: flex;
  align-items: center;

  ::v-deep .v-icon {
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>

<style lang="scss">
// Глобальные стили для popover
.group-select-popover {
  z-index: 3000 !important;
  padding: 0 !important;
}
</style>
